/**
 * THE ACQUISITION CONTEXT ENVELOPE - the shape, and the browser half.
 *
 * One envelope per visit, minted in the browser on the first page it sees,
 * carried by every CTA click, and bound server-side to the application and the
 * purchase. It answers the question `article_origin` could not: an event
 * property computed off the referrer lives in PostHog and nowhere else, so
 * "did the article library produce a sale" had no durable join at all.
 *
 * GENERIC BY DESIGN. `sourceKind` carries nine values rather than "article",
 * because email, social and paid need the same envelope, and a second parallel
 * attribution mechanism for each of them is the drift this exists to end.
 *
 * TWO SHAPES, AND THEY ARE NOT THE SAME SHAPE.
 *
 *   AcquisitionContextClaim  what the BROWSER sends. It carries only what a
 *                            browser can honestly know: the id it minted, the
 *                            path it landed on, the CTA that was clicked, the
 *                            campaign, and a non-authoritative channel hint.
 *   AcquisitionContext       what is STORED. `sourceKind`, `sourceId` and
 *                            `offerId` are the SERVER's decision, derived from
 *                            the landing path against the offer catalog and
 *                            the situation registry (web/lib/acquisition/
 *                            resolve.ts). The browser never decides what a
 *                            path means, exactly as situation-analytics.ts
 *                            already refuses to let it, and for the same two
 *                            reasons: a client-supplied value must not become
 *                            a stored fact, and the registries must not be
 *                            dragged into every page's bundle.
 *
 * THIS FILE IS CLIENT-SAFE. No `server-only`, no registry import, no database.
 * It is bundled into every page through instrumentation-client.ts, which mints
 * the context the way it already captures first touch and the ad click id.
 *
 * STORAGE IS sessionStorage, NOT localStorage, and that is the whole meaning
 * of "a visit". First touch (localStorage) is the browser's first ever visit
 * and never moves; the acquisition context is THIS visit and is minted fresh
 * on the next one, because the question it answers is which page sent him this
 * time. A browser with storage disabled degrades to no envelope at all, which
 * is exactly what a row captured before this existed carries.
 */

/** Bumped when the stored shape changes meaning, so an old row can be read as
 *  what it actually was rather than reinterpreted under a newer rule. */
export const ACQUISITION_SCHEMA_VERSION = "acq-1"

/** sessionStorage key holding this visit's envelope as JSON. */
export const ACQUISITION_CONTEXT_KEY = "ca_acq_ctx"

/**
 * What kind of thing produced this visit.
 *
 * The first three are OUR pages, the next four are external channels, and the
 * last two are the honest absences. The precedence between them is the
 * server's and lives in resolve.ts; this is only the vocabulary, and it is
 * pinned identically by the CHECK on `acquisition_contexts.source_kind`.
 */
export const ACQUISITION_SOURCE_KINDS = [
  "article",
  "situation",
  "product",
  "email",
  "social",
  "paid",
  "referral",
  "direct",
  "unknown",
] as const
export type AcquisitionSourceKind = (typeof ACQUISITION_SOURCE_KINDS)[number]

/**
 * The channel a browser can guess at from its own URL and referrer.
 *
 * NON-AUTHORITATIVE, and named `hint` so nothing downstream forgets it. The
 * server uses it only when the landing path is not one of ours, because losing
 * article-ness for a man who arrived from a newsletter would make the one
 * question this table exists to answer unanswerable.
 */
export const ACQUISITION_CHANNEL_HINTS = ["email", "social", "paid", "referral", "direct"] as const
export type AcquisitionChannelHint = (typeof ACQUISITION_CHANNEL_HINTS)[number]

/**
 * The real channel, stored beside `source_kind` rather than inside it.
 *
 * `source_kind` mixes page type and channel (an article reached from ChatGPT is
 * "article"), so the AI-assistant versus Google split could not be read from
 * the database. This vocabulary is derived once, deterministically, from the
 * claim's channel hint, utm_source and referrer host by acquisitionChannelFrom().
 * It is plain text in the database with no CHECK, so a widening is a code edit
 * and a write can never be rejected on the money path.
 */
export const ACQUISITION_CHANNELS = [
  "google_organic",
  "bing_organic",
  "other_search",
  "ai_assistant",
  "social",
  "paid",
  "email",
  "referral",
  "direct",
] as const
export type AcquisitionChannel = (typeof ACQUISITION_CHANNELS)[number]

/** The stored envelope. `capturedAt` is an ISO instant. */
export type AcquisitionContext = {
  sourceKind: AcquisitionSourceKind
  sourceId?: string
  landingPath: string
  ctaId?: string
  ctaPlacement?: string
  /** A registered OfferId from web/lib/offer-catalog.ts, when the landing page
   *  sells one. Typed as a plain string here so this module stays free of the
   *  catalog import; resolve.ts produces only real ids. */
  offerId?: string
  campaignId?: string
  /** The real channel, derived server-side from the claim. */
  acquisitionChannel?: AcquisitionChannel
  capturedAt: string
  schemaVersion: string
}

/** What the browser posts. Snake case because it rides in the same JSON bodies
 *  as `entry_page` / `ad_click_id` / `originPath`, which are already the wire
 *  vocabulary of those two routes. */
export type AcquisitionContextClaim = {
  id: string
  landing_path: string
  cta_id?: string
  cta_placement?: string
  campaign_id?: string
  channel_hint?: AcquisitionChannelHint
  /** The lowercased utm_source of the landing URL, capped. Input to the
   *  channel only; ChatGPT visits arrive as utm_source with no referrer. */
  utm_source?: string
  /** The external referrer host, lowercased, capped. Absent for direct and
   *  same-origin. */
  referrer_host?: string
  captured_at: string
  schema_version: string
}

const PATH_CAP = 200
const LABEL_CAP = 80
const CAMPAIGN_CAP = 120
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** The ad click params that mean this visit was bought. The same four
 *  captureAdClickId() recognises; kept as its own list because that helper
 *  stores a value and this one only needs to know one was present. */
const PAID_CLICK_PARAMS = ["fbclid", "gclid", "ttclid", "msclkid"] as const

const EMAIL_MEDIUMS = new Set(["email", "e-mail", "newsletter", "mail"])
const PAID_MEDIUMS = new Set(["cpc", "ppc", "paid", "paid_social", "paidsocial", "display", "banner"])
const SOCIAL_MEDIUMS = new Set(["social", "social_organic", "socialorganic", "organic_social", "ig"])

/** Referrer hosts that mean a social visit rather than a plain referral. */
const SOCIAL_HOSTS = [
  "instagram.com",
  "facebook.com",
  "fb.com",
  "tiktok.com",
  "youtube.com",
  "youtu.be",
  "twitter.com",
  "x.com",
  "threads.net",
  "threads.com",
  "bsky.app",
  "reddit.com",
  "pinterest.com",
  "linkedin.com",
]

/** A lowercased uuid, or null. */
export function asAcquisitionId(value: unknown): string | null {
  return typeof value === "string" && UUID_RE.test(value.trim()) ? value.trim().toLowerCase() : null
}

/** True for a value in the channel-hint vocabulary. */
export function isAcquisitionChannelHint(value: unknown): value is AcquisitionChannelHint {
  return typeof value === "string" && (ACQUISITION_CHANNEL_HINTS as readonly string[]).includes(value)
}

/** True for a value in the source-kind vocabulary. */
export function isAcquisitionSourceKind(value: unknown): value is AcquisitionSourceKind {
  return typeof value === "string" && (ACQUISITION_SOURCE_KINDS as readonly string[]).includes(value)
}

/**
 * A same-origin pathname, or undefined.
 *
 * The same acceptance rule sameOriginPath() applies in situation-analytics.ts:
 * one leading slash (a protocol-relative `//host` is a different origin), no
 * whitespace, capped so a hostile value cannot bloat a row.
 */
export function acquisitionPath(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) return undefined
  if (/\s/.test(trimmed)) return undefined
  return trimmed.slice(0, PATH_CAP)
}

function label(value: unknown, cap: number): string | undefined {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim()
  return trimmed ? trimmed.slice(0, cap) : undefined
}

/**
 * The channel this visit looks like, from the URL query and the referrer.
 *
 * Pure, so it is testable without a browser: `search` is a location.search
 * string and `referrer` is document.referrer. A paid medium wins, then an
 * explicit organic medium (social, email), then a bare paid click id; an
 * explicit medium outranks the referrer because it was deliberate; the
 * referrer is the last resort and only distinguishes social from a plain
 * referral.
 */
export function channelHintFrom(search: string, referrer: string, host: string): AcquisitionChannelHint {
  let params: URLSearchParams
  try {
    params = new URLSearchParams(search)
  } catch {
    params = new URLSearchParams()
  }
  const medium = params.get("utm_medium")?.trim().toLowerCase() ?? ""
  // A paid medium, then an explicit organic one, then the bare click id: Meta
  // adds fbclid to every outbound link, organic bio taps included, so a click
  // id alone cannot outrank a medium typed on purpose, but a click id with no
  // medium is the only proof an untagged boost carries.
  if (PAID_MEDIUMS.has(medium)) return "paid"
  if (EMAIL_MEDIUMS.has(medium)) return "email"
  if (SOCIAL_MEDIUMS.has(medium)) return "social"
  for (const key of PAID_CLICK_PARAMS) {
    if (params.get(key)?.trim()) return "paid"
  }

  if (!referrer) return "direct"
  let referrerHost: string
  try {
    referrerHost = new URL(referrer).hostname.toLowerCase()
  } catch {
    return "direct"
  }
  if (!referrerHost || referrerHost === host.toLowerCase()) return "direct"
  const bare = referrerHost.replace(/^www\./, "")
  if (SOCIAL_HOSTS.some((known) => bare === known || bare.endsWith(`.${known}`))) return "social"
  return "referral"
}

/** The external referrer host of this visit, lowercased and capped, or
 *  undefined for no referrer, a malformed one, or this site itself. */
export function referrerHostFrom(referrer: string, host: string): string | undefined {
  if (!referrer) return undefined
  try {
    const referrerHost = new URL(referrer).hostname.toLowerCase()
    if (!referrerHost || referrerHost === host.toLowerCase()) return undefined
    return referrerHost.slice(0, LABEL_CAP)
  } catch {
    return undefined
  }
}

/** The `utm_source` this visit carried, lowercased and capped, or undefined. */
export function utmSourceFrom(search: string): string | undefined {
  try {
    return label(new URLSearchParams(search).get("utm_source"), LABEL_CAP)?.toLowerCase()
  } catch {
    return undefined
  }
}

const AI_ASSISTANT_NAMES = new Set([
  "chatgpt.com",
  "chatgpt",
  "chat.openai.com",
  "openai",
  "perplexity.ai",
  "perplexity",
  "claude.ai",
  "claude",
  "gemini.google.com",
  "gemini",
  "copilot.microsoft.com",
  "copilot",
])
const AI_ASSISTANT_HOSTS = [
  "chatgpt.com",
  "chat.openai.com",
  "perplexity.ai",
  "claude.ai",
  "gemini.google.com",
  "copilot.microsoft.com",
]
// Exact hosts, not subdomain matches: mail.yahoo.com is not search.
const OTHER_SEARCH_HOSTS = [
  "duckduckgo.com",
  "search.brave.com",
  "search.yahoo.com",
  "yahoo.com",
  "ecosia.org",
  "startpage.com",
  "qwant.com",
  "yandex.com",
  "baidu.com",
]

function bareHost(host: string): string {
  return host.toLowerCase().replace(/^www\./, "")
}

/**
 * The real channel of a visit, from what the claim carries.
 *
 * Pure and deterministic, and the SAME function runs in the browser (to stamp
 * an event property from the claim in sessionStorage) and on the server (to
 * store the column), so the event and the row cannot disagree. Precedence:
 * paid and email (the hint's deliberate signals) first, then AI assistants by
 * utm_source or referrer host, then social, then search by referrer host, then
 * the hint's referral and direct. A claim minted before it carried utm_source
 * and referrer_host resolves from its hint alone; one with no hint is
 * undefined, never a guess.
 */
export function acquisitionChannelFrom(input: {
  channel_hint?: AcquisitionChannelHint
  utm_source?: string
  referrer_host?: string
}): AcquisitionChannel | undefined {
  const hint = input.channel_hint
  if (hint === "paid" || hint === "email") return hint
  const source = input.utm_source?.trim().toLowerCase()
  const host = input.referrer_host ? bareHost(input.referrer_host) : ""
  if (source && AI_ASSISTANT_NAMES.has(source)) return "ai_assistant"
  if (host && AI_ASSISTANT_HOSTS.some((known) => host === known || host.endsWith(`.${known}`))) {
    return "ai_assistant"
  }
  if (hint === "social") return "social"
  if (host) {
    if (/^google\.[a-z]{2,3}(\.[a-z]{2})?$/.test(host)) return "google_organic"
    if (host === "bing.com") return "bing_organic"
    if (OTHER_SEARCH_HOSTS.includes(host)) {
      return "other_search"
    }
  }
  return hint
}

/** The `utm_campaign` this visit carried, or undefined. */
export function campaignIdFrom(search: string): string | undefined {
  try {
    return label(new URLSearchParams(search).get("utm_campaign"), CAMPAIGN_CAP)
  } catch {
    return undefined
  }
}

/** A stored claim, validated field by field. Null when the id is not a uuid or
 *  the landing path is not one of ours, because a claim missing either of
 *  those two cannot be recorded. */
export function normalizeAcquisitionClaim(value: unknown): AcquisitionContextClaim | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  const raw = value as Record<string, unknown>
  const id = asAcquisitionId(raw.id)
  const landingPath = acquisitionPath(raw.landing_path)
  if (!id || !landingPath) return null
  const capturedAt = typeof raw.captured_at === "string" ? raw.captured_at.trim() : ""
  const parsed = capturedAt ? Date.parse(capturedAt) : Number.NaN
  const claim: AcquisitionContextClaim = {
    id,
    landing_path: landingPath,
    captured_at: Number.isFinite(parsed) ? new Date(parsed).toISOString() : new Date().toISOString(),
    schema_version: label(raw.schema_version, 16) ?? ACQUISITION_SCHEMA_VERSION,
  }
  const ctaId = label(raw.cta_id, LABEL_CAP)
  if (ctaId) claim.cta_id = ctaId
  const ctaPlacement = label(raw.cta_placement, LABEL_CAP)
  if (ctaPlacement) claim.cta_placement = ctaPlacement
  const campaignId = label(raw.campaign_id, CAMPAIGN_CAP)
  if (campaignId) claim.campaign_id = campaignId
  if (isAcquisitionChannelHint(raw.channel_hint)) claim.channel_hint = raw.channel_hint
  const utmSource = label(raw.utm_source, LABEL_CAP)?.toLowerCase()
  if (utmSource) claim.utm_source = utmSource
  const referrerHost = label(raw.referrer_host, LABEL_CAP)?.toLowerCase()
  if (referrerHost) claim.referrer_host = referrerHost
  return claim
}

// ---------------------------------------------------------------------------
// The browser half
// ---------------------------------------------------------------------------

function readStored(): AcquisitionContextClaim | null {
  if (typeof window === "undefined") return null
  try {
    const raw = window.sessionStorage.getItem(ACQUISITION_CONTEXT_KEY)
    return raw ? normalizeAcquisitionClaim(JSON.parse(raw)) : null
  } catch {
    // Storage unavailable, or a value another version wrote. Either way there
    // is no envelope, which is the same as a visit before this existed.
    return null
  }
}

function writeStored(claim: AcquisitionContextClaim): void {
  try {
    window.sessionStorage.setItem(ACQUISITION_CONTEXT_KEY, JSON.stringify(claim))
  } catch {
    // Private mode. The claim still rides this page's requests in memory.
  }
}

let inMemory: AcquisitionContextClaim | null = null

/**
 * Mint this visit's envelope on the first page that runs, and keep it for the
 * rest of the visit.
 *
 * Called from instrumentation-client.ts beside captureFirstTouch() and
 * captureAdClickId(), so every page mints it with no page needing to know.
 * Idempotent: a visit that already has one keeps it, which is what makes
 * `landing_path` mean the page the visit STARTED on rather than the page he
 * happens to be reading.
 */
export function captureAcquisitionContext(): AcquisitionContextClaim | null {
  if (typeof window === "undefined") return null
  const existing = readStored() ?? inMemory
  if (existing) {
    inMemory = existing
    return existing
  }
  const landingPath = acquisitionPath(window.location.pathname)
  if (!landingPath) return null
  const claim: AcquisitionContextClaim = {
    id: mintAcquisitionId(),
    landing_path: landingPath,
    captured_at: new Date().toISOString(),
    schema_version: ACQUISITION_SCHEMA_VERSION,
    channel_hint: channelHintFrom(window.location.search, document.referrer, window.location.hostname),
  }
  const campaignId = campaignIdFrom(window.location.search)
  if (campaignId) claim.campaign_id = campaignId
  const utmSource = utmSourceFrom(window.location.search)
  if (utmSource) claim.utm_source = utmSource
  const referrerHost = referrerHostFrom(document.referrer, window.location.hostname)
  if (referrerHost) claim.referrer_host = referrerHost
  inMemory = claim
  writeStored(claim)
  return claim
}

/** This visit's envelope, or null when none was minted. */
export function readAcquisitionContext(): AcquisitionContextClaim | null {
  if (typeof window === "undefined") return null
  const stored = readStored() ?? inMemory
  if (stored) inMemory = stored
  return stored
}

/** A uuid, from the platform generator where it exists and from
 *  crypto.getRandomValues where it does not (older Safari serves
 *  randomUUID only on a secure origin). */
function mintAcquisitionId(): string {
  const cryptoObj = typeof globalThis !== "undefined" ? globalThis.crypto : undefined
  if (cryptoObj?.randomUUID) return cryptoObj.randomUUID()
  const bytes = new Uint8Array(16)
  if (cryptoObj?.getRandomValues) cryptoObj.getRandomValues(bytes)
  else for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
